import { Box, IconButton, Paper, Tab, Tabs, Typography } from "@mui/material"
import { ChevronLeftOutlined } from "@mui-symbols-material/w300"
import { Link, Outlet, useLocation, useParams } from "@tanstack/react-router"
import type { FC } from "react"
import { useState } from "react"

import { WarehouseProvider } from "../contexts"
import { useGetWarehouse } from "../hooks"

import WarehouseFormDialog from "./WarehouseEdit/WarehouseFormDialog"

import { Button } from "@/components/common/Button"
import { PermissionActionType } from "@/constants/permissions"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { isAkrochemManagedWarehouse } from "@/screens/WarehouseReceiving/utils"

const WarehouseDetailsLayout: FC = () => {
  const location = useLocation()
  const { warehouseId } = useParams({
    from: "/app/admin/warehouses/$warehouseId",
  })
  const { warehouse, refetch } = useGetWarehouse(warehouseId)
  const { hasPermissions } = usePermissionManager()

  const isAkrochemManaged = isAkrochemManagedWarehouse(warehouse?.type)

  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false)

  const tabs = [
    ...(isAkrochemManaged
      ? [
          {
            label: "Shelving",
            path: `/app/admin/warehouses/${warehouseId}/shelving`,
          },
        ]
      : []),
    {
      label: "Information & Fees",
      path: `/app/admin/warehouses/${warehouseId}/info`,
    },
  ]
  const activeTab = tabs.find((tab) => location.pathname.startsWith(tab.path)) ?? tabs[0]

  const handleFormDialogOpen = () => setIsFormDialogOpen(true)

  const handleFormDialogSubmit = () => {
    setIsFormDialogOpen(false)
    refetch()
  }

  const handleFormDialogClose = () => setIsFormDialogOpen(false)

  return (
    warehouse && (
      <WarehouseProvider value={warehouse}>
        <Paper
          elevation={0}
          className='flex min-h-0 grow flex-col overflow-hidden rounded-lg bg-white'
          data-testid='warehouse-details'
        >
          <Box className='flex items-center justify-between pb-8'>
            <Box className='flex w-full items-center gap-4'>
              <Link to={"/app/admin/warehouses"}>
                <IconButton aria-label='back to company' className='rounded bg-gray-200 text-primary'>
                  <ChevronLeftOutlined />
                </IconButton>
              </Link>
              <Typography
                variant='h5'
                color='primary'
                fontWeight='normal'
                component='h1'
                className='mr-auto'
                data-testid='warehouse-name'
              >
                {`${warehouse.location?.company?.name} - ${warehouse.location?.description}`}
              </Typography>
              {hasPermissions([PermissionActionType.WAREHOUSES_MAINTENANCE]) && (
                <Button appearance='outlined' data-testid='warehouse-edit-button' onClick={handleFormDialogOpen}>
                  Edit
                </Button>
              )}
            </Box>
          </Box>
          <Box className='flex w-full flex-1 flex-col overflow-hidden'>
            <Tabs value={activeTab.path} variant='scrollable' scrollButtons='auto'>
              {tabs.map((tab) => (
                <Tab key={tab.path} label={tab.label} value={tab.path} component={Link} to={tab.path} />
              ))}
            </Tabs>
            <Box className='flex min-h-0 grow flex-col pb-0 pt-4'>
              <Outlet />
            </Box>
          </Box>
        </Paper>
        {isFormDialogOpen && (
          <WarehouseFormDialog
            warehouse={warehouse}
            onSubmit={handleFormDialogSubmit}
            onClose={handleFormDialogClose}
          />
        )}
      </WarehouseProvider>
    )
  )
}

export default WarehouseDetailsLayout
